import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Button,
  Rate,
  Upload,
  Typography,
  Image,
  Spin,
  Select
} from "antd";
import type { UploadFile, UploadProps } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import feedbackService from "../../services/feedbackService";
import logoEzWork from "../../assets/ezwork.png";
import imageCompression from "browser-image-compression";
import add_image from "../../assets/image-add-02.svg";
import Footer from "./Footer";
import FeedbackList from "./FeedbackList";

const { Text } = Typography;

const getBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
};

const FeedbackPage: React.FC = () => {
  const navigate = useNavigate();
  const { qr } = useParams<{ qr: string }>();
  const [form] = Form.useForm();
  const [rating, setRating] = useState<number>(5);
  const [comment, setComment] = useState<string>("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [areaData, setAreaData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [fileList1, setFileList1] = useState<UploadFile[]>([]);
  const [fileList2, setFileList2] = useState<UploadFile[]>([]);
  const [fileList3, setFileList3] = useState<UploadFile[]>([]);
  const [feedbackTypeList, setFeedbackTypeList] = useState<any[]>([]);
  const [feedbackType, setFeedbackType] = useState<string | undefined>();
  const [senderName, setSenderName] = useState<string>("");

  const handleChange1: UploadProps["onChange"] = ({ fileList }) => setFileList1(fileList);
  const handleChange2: UploadProps["onChange"] = ({ fileList }) => setFileList2(fileList);
  const handleChange3: UploadProps["onChange"] = ({ fileList }) => setFileList3(fileList);

  const handlePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as File);
    }
    setPreviewImage(file.url || (file.preview as string));
    setPreviewOpen(true);
  };

  const compressImage = async (file: File) => {
    const options = {
      maxSizeMB: 0.2,
      maxWidthOrHeight: 800,
      useWebWorker: true,
    };
    try {
      return await imageCompression(file, options);
    } catch (error) {
      console.error("Lỗi khi nén ảnh:", error);
      return file;
    }
  };

  const onFinish = async () => {
    if (fileList1.length === 0 && fileList2.length === 0 && fileList3.length === 0) {
      alert("Vui lòng thêm ít nhất 1 ảnh!");
      return;
    }

    setLoading(true);
    try {
      const allFiles = [fileList1, fileList2, fileList3].flat();

      const fileArray = await Promise.all(
        allFiles.map(async (file) => {
          if (!file.originFileObj) return null;
          const compressedFile = await compressImage(file.originFileObj);
          const base64String = await getBase64(compressedFile);
          return {
            file_name: file.name,
            file_base64: base64String.split(",")[1],
          };
        })
      );

      const data: any = await feedbackService.insertFeedback({
        company_id: areaData?.area?.company_id,
        point: rating,
        area_id: areaData?.area?.are_id,
        feedback: comment,
        feedback_type_id: feedbackType,
        ...(areaData?.area?.is_user_send === 1 && senderName ? { user_send: senderName } : {}),
        data: fileArray,
      });

      if (data.code === 200) {
        form.resetFields();
        setRating(5);
        setComment("");
        setSenderName("");
        setFileList1([]);
        setFileList2([]);
        setFileList3([]);
        navigate("/qr/success", {
          state: {
            title: "Gửi thành công!",
            description:
              "Cảm ơn những ý kiến bạn đã đóng góp. Chúng tôi sẽ ghi nhận và sớm khắc phục",
          },
        });
      } else {
        navigate("/qr/error", {
          state: {
            title: "Gửi không thành công!",
            description: data.description || "Có lỗi xảy ra, vui lòng thử lại sau.",
          },
        });
      }
    } catch (error) {
      navigate("/qr/error", {
        state: {
          title: "Gửi không thành công!",
          description: "Không thể kết nối tới server. Vui lòng thử lại sau.",
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchFeedbackTypes = async (companyId: number) => {
    try {
      const data = await feedbackService.get_feedback_type({ company_id: companyId });
      if (data.code === 200 && Array.isArray(data.result)) {
        setFeedbackTypeList(data.result);
      }
    } catch (err) {
      console.error("Lỗi khi lấy danh sách loại phản hồi:", err);
    }
  };

  useEffect(() => {
    if (qr && qr !== "0") {
      setLoading(true);
      feedbackService
        .getAreaByCode(qr)
        .then((data: any) => {
          if (data.code !== 200) {
            navigate("/qr/error", {
              state: {
                title: "Vui lòng thử lại sau!",
                description: `${data.description}`,
              },
            });
          } else {
            setAreaData(data);
            fetchFeedbackTypes(data?.area?.company_id);
          }
        })
        .catch(() => {
          navigate("/qr/error", {
            state: {
              title: "Vui lòng thử lại sau!",
              description: "Không thể kết nối tới server. Vui lòng thử lại sau.",
            },
          });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [qr, navigate]);

  return (
    <Spin spinning={loading} size="large">
      <div style={styles.container}>
        <div style={styles.feedbackForm}>
          <div>
            {areaData?.area && (
              <>
                <div style={styles.logoContainer}>
                  <img
                    src={areaData?.area?.logo_url?.trim() || logoEzWork}
                    alt="ezWork Logo"
                    style={styles.logo}
                  />
                </div>

                <div style={{ padding: "24px 24px 0 24px" }}>
                  <div style={styles.infoSection}>
                    <div style={styles.infoItem}>
                      <Text style={styles.infoLabel}>
                        Đơn vị:{" "}
                        <Text style={styles.infoValue}>
                          {areaData?.area?.real_company_name}
                        </Text>
                      </Text>
                    </div>
                    <div style={styles.infoItem}>
                      <Text style={styles.infoLabel}>
                        Khu vực:{" "}
                        <Text style={styles.infoValue}>
                          {areaData?.area?.are_name}
                        </Text>
                      </Text>
                    </div>
                  </div>

                  <Form
                    requiredMark={false}
                    form={form}
                    name="feedback_form"
                    onFinish={onFinish}
                    layout="vertical"
                    style={styles.form}
                    initialValues={{ rating: rating }}
                  >
                    <Form.Item
                      label={
                        <Text style={styles.formLabel}>
                          Đánh giá của bạn <span style={{ color: "red" }}> *</span>
                        </Text>
                      }
                      name="rating"
                      rules={[{ required: true, message: "Vui lòng chọn đánh giá sao!" }]}
                      style={{ width: "100%" }}
                    >
                      <div style={{ width: "100%", display: "flex", justifyContent: "space-between", }}>
                        <Rate
                          value={rating}
                          onChange={(value) => setRating(value)}
                          style={{ fontSize: 58, color: "#FADB14", width: "100%" }}
                        />
                      </div>
                    </Form.Item>

                    <Form.Item
                      label={
                        <Text style={styles.formLabel}>
                          Bạn muốn phản hồi về <span style={{ color: "red" }}> *</span>
                        </Text>
                      }
                      name="feedback_type"
                      rules={[{ required: true, message: "Vui lòng chọn loại phản hồi!" }]}
                    >
                      <Select
                        placeholder="Chọn loại phản hồi"
                        onChange={(value) => setFeedbackType(value)}
                        value={feedbackType}
                      >
                        {feedbackTypeList.map((item) => (
                          <Select.Option key={item.feedback_type_id} value={item.feedback_type_id}>
                            {item.feedback_type_name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>

                    {areaData?.area?.is_user_send === 1 && (
                      <Form.Item
                        label={
                          <Text style={styles.formLabel}>
                            Tên người gửi <span style={{ color: "red" }}>*</span>
                          </Text>
                        }
                        name="sender_name"
                        rules={[{ required: true, message: "Vui lòng nhập tên người gửi!" }]}
                      >
                        <Input
                          value={senderName}
                          onChange={(e) => setSenderName(e.target.value)}
                          placeholder="Nhập họ và tên của bạn"
                          style={styles.input}
                        />
                      </Form.Item>
                    )}

                    <Form.Item
                      label={<Text style={styles.formLabel}>Ý kiến của bạn</Text>}
                    >
                      <Input.TextArea
                        value={comment}
                        onBlur={(e) => e.target.blur()}
                        onChange={(e) => setComment(e.target.value)}
                        rows={4}
                        placeholder="Nếu bạn có ý kiến hoặc nhận xét gì, hãy viết ở đây...."
                        style={styles.input}
                      />
                    </Form.Item>

                    <Form.Item label={<Text style={styles.formLabel}>Thêm ảnh <span style={{ color: "red" }}> *</span></Text>}>
                      <div style={{ display: "flex", gap: 10 }}>
                        {[fileList1, fileList2, fileList3].map((fileList, index) => (
                          <Upload
                            key={index}
                            accept="image/*"
                            listType="picture-card"
                            fileList={fileList}
                            onPreview={handlePreview}
                            onChange={
                              index === 0
                                ? handleChange1
                                : index === 1
                                  ? handleChange2
                                  : handleChange3
                            }
                            beforeUpload={(file) => {
                              if (!file.type.startsWith("image/")) {
                                alert("Chỉ được tải lên ảnh!");
                                return false;
                              }
                              return false;
                            }}
                          >
                            {fileList.length >= 1 ? null : (
                              <img
                                src={add_image}
                                alt="Add"
                                style={styles.addImageButton}
                              />
                            )}
                          </Upload>
                        ))}
                      </div>
                    </Form.Item>

                    {previewImage && (
                      <Image
                        wrapperStyle={{ display: "none" }}
                        preview={{
                          visible: previewOpen,
                          onVisibleChange: (visible) => setPreviewOpen(visible),
                          afterOpenChange: (visible) =>
                            !visible && setPreviewImage(""),
                        }}
                        src={previewImage}
                      />
                    )}

                    <div style={{ display: "flex", justifyContent: "center", height: 80 }}>
                      <Form.Item>
                        <Button
                          type="primary"
                          htmlType="submit"
                          block
                          loading={loading}
                          style={styles.submitButton}
                        >
                          Gửi đánh giá
                        </Button>
                      </Form.Item>
                    </div>
                  </Form>
                  {areaData && <FeedbackList areaData={areaData} />}
                </div>
              </>
            )}
          </div>
          <Footer />
        </div>
      </div>
    </Spin>
  );
};

// Định nghĩa styles
const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    backgroundColor: "#F1F2F8",
    padding: "25px",
  },
  feedbackForm: {
    backgroundColor: "white",
    borderRadius: 20,
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
    maxWidth: "400px",
    position: "relative",
  },
  logoContainer: {
    display: "flex",
    justifyContent: "center",
    marginBottom: 24,
  },
  logo: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
  },
  infoSection: {
    marginBottom: 16,
  },
  infoItem: {
    display: "flex",
    marginBottom: 8,
  },
  infoLabel: {
    fontSize: 18,
    fontWeight: 500,
    color: "#000",
    marginRight: 6,
  },
  infoValue: {
    fontSize: 18,
    fontWeight: 600,
    color: "#2196F3",
  },
  form: {
    width: "100%",
  },
  formLabel: {
    fontSize: 16,
    fontWeight: 500,
    color: "#333",
  },
  input: {
    width: "100%",
    borderRadius: 12,
    border: "1px solid #E0E0E0",
    padding: 12,
    fontSize: 14,
    resize: "none",
  },
  uploadContainer: {
    display: "flex",
    gap: 10,
  },
  addImageButton: {
    width: "50%",
    height: "50%",
    objectFit: "contain",
    padding: 0,
    border: "none",
    background: "none",
    cursor: "pointer",
  },
  plusIcon: {
    fontSize: 24,
    color: "#999",
  },
  submitButton: {
    backgroundColor: "#2E7CF6",
    borderColor: "#E0E0E0",
    color: "#f8f8f8",
    height: 48,
    fontSize: 16,
    fontWeight: 500,
    borderRadius: 8,
    marginTop: 16,
    boxShadow: "none",
    padding: "0 64px",
  },
};

export default FeedbackPage;